import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  BULK_INVITE_ANALYTICS_EVENT,
  DEFAULT_IMPORT_ROLE,
  REQUIRED_FIELD_KEYS,
} from 'src/app/BulkInvite/bulkInvite.constants'
import {
  BulkInviteAssignableRole,
  ColumnMapping,
  ExtendFieldKey,
  WizardStepId,
} from 'src/app/BulkInvite/bulkInvite.types'
import { autoMapColumns } from 'src/app/BulkInvite/utils/autoMap'
import { logBulkInviteError } from 'src/app/BulkInvite/utils/logBulkInviteError'
import {
  coerceBulkInviteRole,
  getAvailableBulkInviteRoles,
} from 'src/app/BulkInvite/utils/roles'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import {
  BulkInviteMapping,
  BulkInviteResponse,
  BulkInviteRowResponse,
} from 'src/queries/bulkInvite/bulkInvite.models'
import {
  BulkInviteError,
  useSaveBulkInviteMapping,
  useSendBulkInvites,
  useUploadBulkInvite,
} from 'src/queries/bulkInvite/bulkInvite.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import {
  parseBackendErrors,
  useNormalizeBackendError,
} from 'src/utils/error.utils'

const STEP_ORDER: WizardStepId[] = ['upload', 'mapping', 'review']

const EMPTY_MAPPING: ColumnMapping = {
  firstName: null,
  lastName: null,
  email: null,
  phone: null,
  role: null,
}

/** All VALID_NET_NEW rows from a preview — the only rows the send touches. */
export function validNetNewRows(
  invite: BulkInviteResponse | null,
): BulkInviteRowResponse[] {
  if (!invite) return []
  return invite.rows.filter((row) => row.previewCategory === 'VALID_NET_NEW')
}

/** CSV rows that will not be invited — informational, read-only in Review. */
export function otherPreviewRows(
  invite: BulkInviteResponse | null,
): BulkInviteRowResponse[] {
  if (!invite) return []
  return invite.rows.filter(
    (row) =>
      row.sourceType === 'CSV_ROW' && row.previewCategory !== 'VALID_NET_NEW',
  )
}

interface BulkInviteContextValue {
  // Flow
  step: WizardStepId
  goToStep: (step: WizardStepId) => void
  back: () => void

  // Upload
  headers: string[]
  fileName: string | null
  uploading: boolean
  uploadError: boolean
  /** Backend actionable message for a failed upload (falls back to generic). */
  uploadErrorDetail: string | null
  /** Non-fatal upload warnings surfaced on the Mapping step. */
  uploadWarnings: string[]
  acceptFile: (file: File) => void
  resetUpload: () => void

  // Mapping
  mapping: ColumnMapping
  setMapping: (field: ExtendFieldKey, header: string | null) => void
  isMappingComplete: boolean
  savingMapping: boolean
  mappingError: boolean
  /** Backend actionable message for a failed mapping save. */
  mappingErrorDetail: string | null
  submitMapping: () => void

  // Preview (Review)
  preview: BulkInviteResponse | null
  invitedRows: BulkInviteRowResponse[]
  otherRows: BulkInviteRowResponse[]
  invitedCount: number
  availableInviteRoles: BulkInviteAssignableRole[]
  selectedInviteRolesByRowId: Record<string, string | undefined>
  getSelectedInviteRole: (
    row: BulkInviteRowResponse,
  ) => BulkInviteAssignableRole
  setSelectedInviteRole: (rowId: string, role: BulkInviteAssignableRole) => void

  // Send
  sending: boolean
  sendError: boolean
  /** Backend actionable message for a failed send. */
  sendErrorDetail: string | null
  send: () => void
  /**
   * Flips true once a send succeeds. The component layer watches this to fire
   * the async toast, close the modal, and redirect — navigation/Toast concerns
   * stay out of the context.
   */
  submitted: boolean

  // Lifecycle
  reset: () => void
}

/**
 * Which Extend fields a mapping actually fills. These are OUR field keys
 * (`'email'`, `'firstName'`) — never the customer's raw CSV header names, which
 * are uploaded content.
 */
function mappedFieldKeys(mapping: ColumnMapping): ExtendFieldKey[] {
  const keys = Object.keys(mapping) as ExtendFieldKey[]
  return keys.filter((key) => Boolean(mapping[key]))
}

function isSameMapping(a: ColumnMapping, b: ColumnMapping): boolean {
  const keys = Object.keys(a) as ExtendFieldKey[]
  return keys.every((key) => a[key] === b[key])
}

/**
 * Row counts keyed by preview category (`preview_SKIPPED_INVALID: 3`).
 *
 * This is the one signal nothing downstream can reconstruct: it says whether an
 * admin's CSV was actually usable, or whether validation quietly rejected rows
 * they expected to invite. Category keys are backend enum values, never row
 * content.
 */
function previewCategoryCounts(
  invite: BulkInviteResponse,
): Record<string, number> {
  const counts: Record<string, number> = {}
  invite.rows.forEach((row) => {
    if (!row.previewCategory) return
    const key = `preview_${row.previewCategory}`
    counts[key] = (counts[key] ?? 0) + 1
  })
  return counts
}

/**
 * HTTP status for a failed mutation. We deliberately do NOT send the backend's
 * message: it can echo the uploaded file name or row content into Mixpanel.
 */
function errorStatus(error: unknown): number | null {
  return (error as BulkInviteError | null)?.response?.status ?? null
}

const BulkInviteContext = createContext<BulkInviteContextValue | null>(null)

export function useBulkInvite(): BulkInviteContextValue {
  const ctx = useContext(BulkInviteContext)
  if (!ctx) {
    throw new Error('useBulkInvite must be used within a BulkInviteProvider')
  }
  return ctx
}

export const BulkInviteProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const currentUser = useCurrentUser()
  const organizationId = organization?.id
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: organizationId,
  })
  const normalizeBackendError = useNormalizeBackendError()
  const { analytics } = useAnalyticsContext()

  // The auto-mapped columns as first proposed, so `mapping submitted` can
  // report whether the admin had to correct our fuzzy match.
  const autoMappedRef = useRef<ColumnMapping>(EMPTY_MAPPING)

  // Rows whose role the admin actually changed in Review. Tracked explicitly
  // rather than derived by comparing effective vs. default role:
  // getSelectedInviteRole coerces a backend default the admin cannot assign
  // (ADMIN for a non-owner, CARD_MANAGER when the org feature is off) down to
  // MEMBER, so a comparison counts that automatic coercion as a hand
  // correction even when the dropdown was never touched.
  const rolesChangedRowIdsRef = useRef<Set<string>>(new Set())

  const [step, setStep] = useState<WizardStepId>('upload')
  const [bulkInviteId, setBulkInviteId] = useState<string | null>(null)
  const [headers, setHeaders] = useState<string[]>([])
  const [fileName, setFileName] = useState<string | null>(null)
  const [mapping, setMappingState] = useState<ColumnMapping>(EMPTY_MAPPING)
  const [preview, setPreview] = useState<BulkInviteResponse | null>(null)
  const [uploadWarnings, setUploadWarnings] = useState<string[]>([])
  const [submitted, setSubmitted] = useState(false)
  const [selectedInviteRolesByRowId, setSelectedInviteRolesByRowId] = useState<
    Record<string, string | undefined>
  >({})

  const uploadMutation = useUploadBulkInvite()
  const mappingMutation = useSaveBulkInviteMapping()
  const sendMutation = useSendBulkInvites()

  // Pull the backend's actionable message (error.response.data.error) for any
  // failed mutation, falling back to null so the UI shows generic copy.
  const backendDetail = useCallback(
    (error: unknown): string | null => {
      if (!error) return null
      const { topLevelError } = parseBackendErrors(normalizeBackendError(error))
      return topLevelError ?? null
    },
    [normalizeBackendError],
  )

  const availableInviteRoles = useMemo(
    () =>
      getAvailableBulkInviteRoles({
        canInviteAdmin: currentUser?.hasOrgOwnerPermissions,
        // Treat Card Manager as available until the org feature-flag query
        // returns a definitive `false`. While that query is loading or errored,
        // orgFeatureFlags is undefined; excluding Card Manager then would
        // silently coerce a backend-resolved CARD_MANAGER row down to MEMBER on
        // send. Backend send validation stays the final authority if the
        // feature really is off.
        cardManagerRoleEnabled:
          orgFeatureFlags?.cardManagerRoleEnabled !== false,
      }),
    [
      currentUser?.hasOrgOwnerPermissions,
      orgFeatureFlags?.cardManagerRoleEnabled,
    ],
  )

  const defaultRoleForRow = useCallback(
    (row: BulkInviteRowResponse): string =>
      row.inviteRole ??
      row.roleResolution?.resolvedRole ??
      row.role ??
      DEFAULT_IMPORT_ROLE,
    [],
  )

  const seedSelectedInviteRoles = useCallback(
    (data: BulkInviteResponse) => {
      // A fresh preview reseeds every row, so no prior edit survives it.
      rolesChangedRowIdsRef.current = new Set()
      const next: Record<string, string | undefined> = {}
      validNetNewRows(data).forEach((row) => {
        next[row.id] = defaultRoleForRow(row)
      })
      setSelectedInviteRolesByRowId(next)
    },
    [defaultRoleForRow],
  )

  const getSelectedInviteRole = useCallback(
    (row: BulkInviteRowResponse): BulkInviteAssignableRole =>
      coerceBulkInviteRole(
        selectedInviteRolesByRowId[row.id] ?? defaultRoleForRow(row),
        availableInviteRoles,
      ),
    [availableInviteRoles, defaultRoleForRow, selectedInviteRolesByRowId],
  )

  const setSelectedInviteRole = useCallback(
    (rowId: string, role: BulkInviteAssignableRole) => {
      setSelectedInviteRolesByRowId((current) => ({
        ...current,
        [rowId]: role,
      }))
      rolesChangedRowIdsRef.current.add(rowId)
      // Enum value, not the translated display label, and no row identity.
      analytics.track(BULK_INVITE_ANALYTICS_EVENT, {
        action: 'invite role changed',
        role,
      })
    },
    [analytics],
  )

  // ----- Step navigation -----
  const goToStep = useCallback((next: WizardStepId) => setStep(next), [])

  const back = useCallback(() => {
    setStep((current) => {
      const idx = STEP_ORDER.indexOf(current)
      return idx > 0 ? STEP_ORDER[idx - 1] : current
    })
  }, [])

  // ----- Upload -----
  const acceptFile = useCallback(
    (file: File) => {
      if (!organizationId) {
        // Fail loud: we should never reach the upload step without an org.
        logBulkInviteError('bulk_invite.missing_organization', {
          organizationId,
        })
        return
      }
      uploadMutation.reset()
      setFileName(file.name)
      uploadMutation.mutate(
        { organizationId, file },
        {
          onSuccess: (data) => {
            setBulkInviteId(data.bulkInviteId)
            setHeaders(data.headers)
            setFileName(data.fileName)
            // Capture non-fatal warnings (e.g. dropped blank rows) so the
            // Mapping step can surface them — they were previously discarded.
            setUploadWarnings(data.warnings ?? [])
            // Seed the mapping with a best-effort auto-match of the backend
            // headers; the admin confirms/adjusts on the next step.
            const autoMapped = autoMapColumns(data.headers)
            autoMappedRef.current = autoMapped
            setMappingState(autoMapped)
            setStep('mapping')
            // Counts and our own field keys only — never the file name or the
            // customer's raw CSV headers.
            analytics.track(BULK_INVITE_ANALYTICS_EVENT, {
              action: 'upload succeeded',
              row_count: data.rowCount,
              header_count: data.headers.length,
              warning_count: (data.warnings ?? []).length,
              auto_mapped_fields: mappedFieldKeys(autoMapped),
            })
          },
          onError: (error) => {
            analytics.track(BULK_INVITE_ANALYTICS_EVENT, {
              action: 'upload failed',
              error_status: errorStatus(error),
              has_backend_detail: Boolean(backendDetail(error)),
            })
          },
        },
      )
    },
    [organizationId, uploadMutation, analytics, backendDetail],
  )

  // Retry hook for the Upload error state — clears the failed mutation so the
  // dropzone is interactive again instead of dead-ending on the error screen.
  const resetUpload = useCallback(() => {
    uploadMutation.reset()
    setFileName(null)
  }, [uploadMutation])

  // ----- Mapping -----
  const setMapping = useCallback(
    (field: ExtendFieldKey, header: string | null) => {
      setMappingState((current) => {
        const next: ColumnMapping = { ...current }
        // A header can only feed one field — clear it elsewhere first.
        if (header) {
          const fieldKeys = Object.keys(next) as ExtendFieldKey[]
          fieldKeys.forEach((key) => {
            if (next[key] === header) next[key] = null
          })
        }
        next[field] = header
        return next
      })
    },
    [],
  )

  const isMappingComplete = useMemo(
    () => REQUIRED_FIELD_KEYS.every((key) => Boolean(mapping[key])),
    [mapping],
  )

  const submitMapping = useCallback(() => {
    if (!organizationId) {
      logBulkInviteError('bulk_invite.missing_organization', { organizationId })
      return
    }
    if (!bulkInviteId) {
      logBulkInviteError('bulk_invite.missing_bulk_invite_id', {
        organizationId,
      })
      return
    }
    if (!isMappingComplete) return
    const mappingBody: BulkInviteMapping = {
      email: mapping.email as string,
    }
    if (mapping.firstName) mappingBody.firstName = mapping.firstName
    if (mapping.lastName) mappingBody.lastName = mapping.lastName
    if (mapping.phone) mappingBody.phone = mapping.phone
    if (mapping.role) mappingBody.role = mapping.role
    const body: { mapping: BulkInviteMapping } = { mapping: mappingBody }
    mappingMutation.reset()
    analytics.track(BULK_INVITE_ANALYTICS_EVENT, {
      action: 'mapping submitted',
      mapped_fields: mappedFieldKeys(mapping),
      changed_from_automap: !isSameMapping(mapping, autoMappedRef.current),
    })
    mappingMutation.mutate(
      { organizationId, bulkInviteId, body },
      {
        onSuccess: (data) => {
          setPreview(data)
          seedSelectedInviteRoles(data)
          setStep('review')
          analytics.track(BULK_INVITE_ANALYTICS_EVENT, {
            action: 'preview loaded',
            row_count: data.rowCount,
            invited_count: validNetNewRows(data).length,
            ...previewCategoryCounts(data),
          })
        },
        onError: (error) => {
          analytics.track(BULK_INVITE_ANALYTICS_EVENT, {
            action: 'mapping failed',
            error_status: errorStatus(error),
          })
        },
      },
    )
  }, [
    organizationId,
    bulkInviteId,
    isMappingComplete,
    mapping,
    mappingMutation,
    seedSelectedInviteRoles,
    analytics,
  ])

  // ----- Preview derivations -----
  const invitedRows = useMemo(() => validNetNewRows(preview), [preview])
  const otherRows = useMemo(() => otherPreviewRows(preview), [preview])
  const invitedCount = invitedRows.length

  // ----- Send -----
  const send = useCallback(() => {
    if (!organizationId) {
      logBulkInviteError('bulk_invite.missing_organization', { organizationId })
      return
    }
    if (!bulkInviteId) {
      logBulkInviteError('bulk_invite.missing_bulk_invite_id', {
        organizationId,
      })
      return
    }
    if (!preview) {
      logBulkInviteError('bulk_invite.missing_preview', {
        organizationId,
        bulkInviteId,
      })
      return
    }
    // A VALID_NET_NEW row with no email is a backend/contract bug, not a normal
    // skip — silently trimming it would under-send. Log loudly and block the
    // send so the operator notices instead of shipping a partial import.
    const rowsMissingEmail = invitedRows.filter((row) => !row.email)
    if (rowsMissingEmail.length > 0) {
      logBulkInviteError(
        'bulk_invite.invitee_missing_email',
        {
          organizationId,
          bulkInviteId,
          rowIds: rowsMissingEmail.map((row) => row.id),
        },
        new Error(
          `${rowsMissingEmail.length} VALID_NET_NEW row(s) missing email`,
        ),
      )
      return
    }
    // Send EXACTLY all current VALID_NET_NEW rows, each with the role currently
    // selected in Review.
    const invitees = invitedRows.map((row) => ({
      rowId: row.id,
      email: row.email as string,
      role: getSelectedInviteRole(row),
    }))
    // Rows the admin actually re-roled by hand — the signal for whether role
    // resolution is landing on the right default.
    const rolesOverriddenCount = invitedRows.filter((row) =>
      rolesChangedRowIdsRef.current.has(row.id),
    ).length
    sendMutation.reset()
    sendMutation.mutate(
      { organizationId, bulkInviteId, body: { invitees } },
      {
        onSuccess: () => {
          // Signal success to the component layer; it owns the toast, modal
          // close, and redirect (the context stays free of history/Toast).
          setSubmitted(true)
          // NOT a completed send. This response carries status SENDING — the
          // job runs in the background and can still land FAILED or
          // COMPLETED_WITH_FAILURES. Naming it `send accepted` keeps the
          // funnel honest; the terminal outcome would have to come from the
          // polling read (useQueryBulkInvite), which no UI mounts today
          // because the wizard closes on acceptance.
          analytics.track(BULK_INVITE_ANALYTICS_EVENT, {
            action: 'send accepted',
            invited_count: invitees.length,
            roles_overridden_count: rolesOverriddenCount,
          })
        },
        onError: (error) => {
          // The request itself was rejected, so no job was ever queued.
          analytics.track(BULK_INVITE_ANALYTICS_EVENT, {
            action: 'send request failed',
            error_status: errorStatus(error),
            invited_count: invitees.length,
          })
        },
      },
    )
  }, [
    organizationId,
    bulkInviteId,
    preview,
    invitedRows,
    getSelectedInviteRole,
    sendMutation,
    analytics,
  ])

  // ----- Lifecycle -----
  const reset = useCallback(() => {
    setStep('upload')
    setBulkInviteId(null)
    setHeaders([])
    setFileName(null)
    setMappingState(EMPTY_MAPPING)
    setPreview(null)
    setUploadWarnings([])
    setSubmitted(false)
    setSelectedInviteRolesByRowId({})
    rolesChangedRowIdsRef.current = new Set()
    uploadMutation.reset()
    mappingMutation.reset()
    sendMutation.reset()
  }, [uploadMutation, mappingMutation, sendMutation])

  const value: BulkInviteContextValue = {
    step,
    goToStep,
    back,
    headers,
    fileName,
    uploading: uploadMutation.isPending,
    uploadError: uploadMutation.isError,
    uploadErrorDetail: backendDetail(uploadMutation.error),
    uploadWarnings,
    acceptFile,
    resetUpload,
    mapping,
    setMapping,
    isMappingComplete,
    savingMapping: mappingMutation.isPending,
    mappingError: mappingMutation.isError,
    mappingErrorDetail: backendDetail(mappingMutation.error),
    submitMapping,
    preview,
    invitedRows,
    otherRows,
    invitedCount,
    availableInviteRoles,
    selectedInviteRolesByRowId,
    getSelectedInviteRole,
    setSelectedInviteRole,
    sending: sendMutation.isPending,
    sendError: sendMutation.isError,
    sendErrorDetail: backendDetail(sendMutation.error),
    send,
    submitted,
    reset,
  }

  return (
    <BulkInviteContext.Provider value={value}>
      {children}
    </BulkInviteContext.Provider>
  )
}
